import { Button, DialogActions, DialogContent } from "@mui/material";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
// import { OrderType } from "../../directus/collections";
// import { MatchFilter } from "../../libs/statsFilters";
import { FilterForm } from "../components/FilterBar/FilterForm";
import { OrderType } from "../directus/collections";
import { MatchFilter } from "../libs/statsFilters";
import { OpenModalProps, useDialog } from "./DialogProvider";
// import { FilterForm } from "./FilterForm";

interface FilterDialogProps {
  filters?: MatchFilter;
  onChange?: (filters: MatchFilter) => void;
}

export type CityEscapeFiltersOpenModalProps = {
  title?: string;
  filters: MatchFilter;
  loading?: boolean;
  onSave?: (filters: MatchFilter) => void;
};

/**
 * Dialog per modificare i filtri
 */
export const FilterDialog = forwardRef(
  ({ filters, onChange }: FilterDialogProps, ref) => {
    const [localFilters, setLocalFilters] = useState<MatchFilter>(
      filters || {}
    );

    useImperativeHandle(
      ref,
      () => ({
        set: (newFilters: MatchFilter) => {
          setLocalFilters(newFilters);
        },
        reset: () => {
          setLocalFilters(filters || {});
        },
        clear: () => {
          setLocalFilters({});
        },
        value: () => localFilters,
      }),
      [filters, localFilters]
    );

    useEffect(() => {
      onChange?.(localFilters);
    }, [localFilters]);

    // Handlers per ogni tipo di filtro
    const handleOrderTypeChange = (orderTypes: OrderType[]) => {
      setLocalFilters((prev) => ({
        ...prev,
        orderType: orderTypes.length > 0 ? orderTypes : undefined,
      }));
    };

    const handleDurationChange = (duration: {
      min?: number | null;
      max?: number | null;
    }) => {
      const hasValues = duration.min != null || duration.max != null;
      setLocalFilters((prev) => ({
        ...prev,
        duration: { min: duration.min, max: duration.max || null },
      }));
    };

    const handleDateRangeChange = (dateRange: {
      startDate?: number | null;
      endDate?: number | null;
    }) => {
      const hasValues =
        dateRange.startDate != null || dateRange.endDate != null;
      setLocalFilters((prev) => ({
        ...prev,
        dateRange: hasValues ? dateRange : undefined,
      }));
    };

    const handleEmailsChange = (emails: { exclude?: string[] }) => {
      setLocalFilters((prev) => ({
        ...prev,
        emails:
          emails.exclude && emails.exclude.length > 0 ? emails : undefined,
      }));
    };

    const handleChallengeDurationChange = (duration: {
      min?: number | null;
      max?: number | null;
    }) => {
      const hasValues = duration.min != null || duration.max != null;
      setLocalFilters((prev) => ({
        ...prev,
        challenge: hasValues ? { duration } : undefined,
      }));
    };

    const handleStateChange = (
      state?: "created" | "started" | "fulfilled" | "completed"
    ) => {
      setLocalFilters((prev) => ({
        ...prev,
        state: state,
      }));
    };

    const handleProgressChange = (progress: { minChallenges?: number }) => {
      const hasValues = progress.minChallenges != null;
      setLocalFilters((prev) => ({
        ...prev,
        progress: hasValues ? progress : undefined,
      }));
    };

    return (
      <DialogContent dividers sx={{ p: 0 }}>
        <FilterForm
          filters={localFilters}
          onOrderTypeChange={handleOrderTypeChange}
          onDurationChange={handleDurationChange}
          onDateRangeChange={handleDateRangeChange}
          onEmailsChange={handleEmailsChange}
          onChallengeDurationChange={handleChallengeDurationChange}
          onStateChange={handleStateChange}
          onProgressChange={handleProgressChange}
        />
      </DialogContent>
    );
  }
);

export default FilterDialog;

export function useCityEscapeFiltersDialog(id?: string) {
  const dialog = useDialog();
  const dialogId = id ?? "city-escape-filters-dialog";
  const filterFormRef = React.useRef<any>(null);

  useEffect(() => {
    const startingProps: OpenModalProps = {
      loading: true,
      slotProps: { dialog: { maxWidth: "lg" } },
    };

    dialog.create(dialogId, startingProps, { onConflict: "replace" });

    return () => {
      dialog.remove(dialogId);
    };
  }, []);

  const openModal = (props: CityEscapeFiltersOpenModalProps) => {
    dialog.open(dialogId, {
      loading: props.loading,
      title: props.title ?? "Modifica Filtri",
      content: <FilterDialog filters={props.filters} ref={filterFormRef} />,
      actions: (
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => filterFormRef.current?.reset()}
            variant="outlined"
          >
            Annulla
          </Button>
          <Button
            onClick={(e) => {
              props?.onSave?.(filterFormRef.current?.value() || {});
              dialog.close(dialogId);
            }}
            variant="contained"
            color="primary"
          >
            Salva Modifiche
          </Button>
        </DialogActions>
      ),
      slotProps: { dialog: { maxWidth: "lg" } },
    });
  };

  const close = () => {
    dialog.remove(dialogId);
  };

  return {
    openModal,
    close,
  };
}
